<template>
  <component :is="layout">
    <template #headerBar>
      <HeaderBar :modifiers="['transparent', 'more-height']">
        <router-link
          v-if="backUrl"
          class="button"
          :to="backUrl"
        >
          <LeftArrow class="icon" />
        </router-link>
        <template #right>
          <div
            class="button"
            @click="router.push(backUrl);"
          >
            {{ textData.cancel }}
          </div>
          <FormSubmitButton
            :disabled="disableSave"
            :isSaving="saving"
            @click="$emit('save', $route.params.issueId)"
          />
        </template>
      </HeaderBar>
    </template>
    <template #leftContent>
      <template v-if="regionData.length > 0">
        <div class="layout-regions__search-bar show-for-medium">
          <UseSvg
            :class="{ 'icon': true, 'icon--darker': !regionSearch }"
            name="search"
          />
          <input
            v-model="regionSearch"
            class="layout-regions__search-input typo-small-title"
            :placeholder="$t('regions.search_all_regions')"
          >
          <button
            v-if="regionSearch"
            class="button button--tiny button--transparent"
            type="button"
            @click="regionSearch = ''"
          >
            <UseSvg
              class="icon icon--small"
              name="close_circle_16"
            />
          </button>
        </div>

        <RegionTree
          v-if="regionTree"
          :class="{ 'hide-for-medium': regionSearch }"
          :modifiers="['location-scroll']"
          :regionTree="regionTree"
          :selectedRegion="selectedRegion"
          @select="selectRegion"
        />

        <Loader
          v-if="!regionTree"
          class="show-for-medium"
          :modifiers="['height-fix']"
        />

        <EmptyMessage
          v-if="regionSearch && regionResults.length === 0"
          :message="$t('general.no_results')"
          :modifiers="['grow']"
        >
          <UseSvg name="magnifying_glass_circle" />
        </EmptyMessage>

        <template v-if="regionSearch && regionResults.length > 0">
          <div class="layout-regions__results-header typo-small">
            {{ $t('general.results') }}
          </div>
          <div class="layout-regions__regions-tree scroll-container">
            <ul>
              <RegionItem
                v-for="region in regionResults"
                :key="region.id"
                class="layout-regions__regions-tree"
                :ignoreSelected="true"
                :region="region"
                :showChildren="false"
                @select="selectRegionFromSearch"
              />
            </ul>
          </div>
        </template>
      </template>
    </template>
    <template #mainContent>
      <div
        class="map-container"
      >
        <Shaft3dModelToolbar
          v-if="data && !data?.has_pending_tile_process"
          hide3dTools
          @update:model-value="$emit('update:toolbarState', $event)"
        />
        <div class="layout-regions__map-wrapper">
          <div class="layout-regions__map layout-regions__map--center layout-regions__map--height">
            <LeafletMap
              v-if="data && !data?.has_pending_tile_process"
              ref="map"
              :diagram="data?.diagram_identifier"
              :operationBounds="data.bounds"
              :overlays="data.map_tile_overlays"
              @init="$emit('mapInit')"
              @map-click="$emit('mapClick', $event)"
              @polygon-click="selectRegionOnMap"
              @select-operation-bounds="selectNoRegion()"
            />
            <EmptyMessage
              v-else-if="!map && data?.has_pending_tile_process"
              :message="textData.diagram.message"
              :title="textData.diagram.title"
            />
            <EmptyMessage
              v-else
              :message="textData.noHme.message"
              :title="textData.noHme.title"
            >
              <NoMobileAssetsSvg v-if="isHme" />
              <StationSelectSvg v-else />
            </EmptyMessage>
            <Loader
              v-if="loading"
              :modifiers="['overlay']"
            />
            <OutOfBoundsMessage
              v-if="map && isOutOfBounds"
              :modifiers="['elevate']"
            />
          </div>
        </div>
      </div>
    </template>
  </component>
</template>

<script setup>
import { ref, computed, defineAsyncComponent, watch, nextTick, reactive } from 'vue';

// Utils
import { useRouter } from 'vue-router';
import debounce from '@/vendor/debounce';
import regionPolygonOptions from '@/functions/region_polygon_options';
import ancestry from '@/functions/ancestry';
import Searcher from '@/functions/searcher';
import { geoJSONPolygonToLatLngs, rgeoPolygonToGeoJSON } from '@/functions/geospatial_conversions';

// Components
import FormSubmitButton from '@/components/form/form_submit_button.vue';
import HeaderBar from '@/components/generic/header_bar.vue';
import LeafletMap from '@/vendor/components/leaflet_map.vue';
import EmptyMessage from '@/components/generic/empty_message.vue';
import Loader from '@/components/generic/loader.vue';
import Shaft3dModelToolbar from '@/components/shaftsV2/shaft3dModel/shaft_3d_model_toolbar.vue';
import OutOfBoundsMessage from '@/components/generic/out_of_bounds_message.vue';
import RegionItem from '@/components/regions/region_item.vue';
import RegionTree from '@/components/regions/region_tree.vue';

// Svg
import UseSvg from '@/components/generic/use_svg.vue';
import LeftArrow from '@/images/arrow-left.svg';
import StationSelectSvg from '@/images/station-select.svg';
import NoMobileAssetsSvg from '@/images/no-mobile-assets.svg';

const props = defineProps({
  /** The current loading state */
  loading: { type: Boolean, required: true, default: false },
  /** The saving state of the page */
  saving: { type: Boolean, required: true, default: false },
  /** The state of the save button */
  disableSave: { type: Boolean, required: true, default: false },
  /** The state of the toolbar */
  toolbarState: { type: String, default: 'expand' },
  /** Error state of the map */
  isOutOfBounds: { type: Boolean, required: true, default: false },
  /** The type of form in progress */
  isHme: { type: Boolean, required: true, default: false },
  /** Sets the back route */
  backUrl: { type: Object, required: true, default: null },
  /** Hash of most text in the page */
  textData: { type: Object, required: true, default: null },
  /** Hash of regions data in the page */
  regionData: { type: Object, required: true, default: null },
  /** The current form data, new or existing */
  data: { type: Object, required: true, default: null },
  /** The operation type. Can only be `shaft`, `plant`, `ship` at the moment. */
  operationType: {
    type: String,
    required: true,
    default: 'plants',
    validator: (value) => ['plants', 'ships', 'equipment'].includes(value),
  },
});

const router = useRouter();
const map = ref(null);
const showRegions = ref(false);
const regionMap = ref(null);
const regionTree = ref([]);
const selectedRegion = ref(null);
const regionSearch = ref('');
const regionResults = ref([]);

let regionsSearcher = null;

/** Dynamically add the layout based on the type */
const layout = computed(() => {
  return defineAsyncComponent(() => import(`@/components/${props.operationType}/location/issue_location_layout.vue`));
});

watch(props, (value) => {
  regionMap.value = new Map();
  if (!map.value) return; // in case user navigates away

  value?.regionData?.forEach(loadRegion);
  regionTree.value = ancestry(regionMap.value.values());
});

watch(regionSearch, debounce(() => {
  if (!regionSearch.value) return;

  regionResults.value = regionsSearcher.search(regionSearch.value);
}, 300, true));

function loadRegion(region) {
  let geoJSON = rgeoPolygonToGeoJSON(region.bounds);
  let latLngs = geoJSONPolygonToLatLngs(geoJSON);
  let polygon = map.value.addPolygon(latLngs, regionPolygonOptions(region.region_type));
  let regionObject = {
    ...region,
    geoJSON,
    latLngs,
    polygon,
  };
  polygon.disableEdit();
  polygon.region = regionObject;

  regionMap.value.set(region.id, reactive(regionObject));

  regionsSearcher = new Searcher(regionMap.value);
}

/**
 * Make sure clicking on a stack of objects doesn't trigger a click on all of them
 */
let selectingObject = false;
function doSelect() {
  selectingObject = true;
  setTimeout(() => selectingObject = false, 0);
}

function deselectRegion() {
  if (selectedRegion.value) {
    let previousRegion = regionMap.value.get(selectedRegion.value.id);
    previousRegion.polygon.setStyle(regionPolygonOptions(previousRegion.region_type, false));
    previousRegion.selected = false;
  }
}

async function selectRegion(region, { goTo = true } = {}) {
  if (!region) return selectNoRegion({ goTo });
  if (selectingObject && goTo) return;
  selectingObject = true;

  showRegions.value = false;
  deselectRegion();

  let newRegion = regionMap.value.get(region.id);
  newRegion.polygon.setStyle(regionPolygonOptions(newRegion.region_type, true));
  selectedRegion.value = newRegion;

  if (goTo) map.value.goToObject(newRegion.polygon);

  await nextTick();

  await new Promise(resolve => {
    newRegion.selected = true;
    setTimeout(() => {
      selectingObject = false;
      resolve();
    }, 0);
  });
}

function selectRegionOnMap(polygon) {
  if (polygon.region) {
    selectRegion(polygon.region);
  }
}

function selectNoRegion({ goTo = true } = {}) {
  if (selectingObject && goTo) return;
  doSelect();

  router.replace({ query: { regionId: null } });

  showRegions.value = false;
  deselectRegion();

  selectedRegion.value = null;

  if (goTo) {
    map.value.goToOperationBounds();
  }
}

async function selectRegionFromSearch(region) {
  regionSearch.value = '';
  await selectRegion(region);
}

defineEmits([
  'save', 'mapInit', 'mapClick', 'update:toolbarState',
]);

defineExpose({map});
</script>
